// Bileli · modules/media/model — media-upload REPOSITORY (G2/WS-A). The two JSON control-plane calls
// (initiate, finalize) go through the cookie-authed bileliApiJson/Fetch; the BYTE push goes DIRECT to
// storage with NO cookies (the presigned/one-time URL or tus signature is the credential):
//   S3_PUT (PDF → R2/MinIO)        = raw fetch PUT
//   CF_DIRECT_FORM (IMAGE → CF)    = XHR multipart POST (XHR for upload progress events)
//   BUNNY_TUS (VIDEO → Bunny)      = tus-js-client, resumable: asset-scoped fingerprint + localStorage
//                                    urlStorage → findPreviousUploads resumes at the server byte offset.
// VIDEO runs "background-style": initiateVideoUpload persists the File (IndexedDB) + ticket (localStorage)
// and returns immediately — the engine pushes the bytes (see engine/uploadEngine).

import * as tus from 'tus-js-client';

import type { MediaAsset, MediaKind, UploadTicket } from './media.types';

import { putUploadFile } from '../lib/idbBlobStore';
import { savePendingUpload } from '../lib/pendingUploadStore';
import { bileliApiFetch, bileliApiJson, parseApi, schemas } from '../../../shared';
import {
  DEFAULT_POLL_MS, isPastDeadline, nextPollDelayMs, probeFromState, shouldRefreshProgress,
} from './finalize-poll';
import { MEDIA_CONTENT_TYPES, MEDIA_MAX_BYTES } from './media.types';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export class MediaValidationError extends Error {}
export class MediaApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

export function validateFile(kind: MediaKind, file: File): void {
  if (!MEDIA_CONTENT_TYPES[kind].includes(file.type)) {
    throw new MediaValidationError('Неподдерживаемый тип файла.');
  }
  if (file.size > MEDIA_MAX_BYTES[kind]) {
    const mb = Math.round(MEDIA_MAX_BYTES[kind] / (1024 * 1024));
    throw new MediaValidationError(`Файл слишком большой (макс. ${mb} МБ).`);
  }
}

interface InitiateBody { orgId: string; kind: MediaKind; contentType: string; originalFilename?: string }

export async function initiateUpload(body: InitiateBody): Promise<UploadTicket> {
  const raw = await bileliApiJson<unknown>('/media/uploads', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return parseApi(schemas.UploadTicketSchema, raw, 'POST /media/uploads') as UploadTicket;
}

// Asset-scoped fingerprint: concurrent uploads of the SAME file get distinct tus resume slots.
function fingerprintFor(assetId: string): tus.UploadOptions['fingerprint'] {
  return (file: File | Blob | Pick<ReadableStreamDefaultReader, 'read'>) => {
    const f = file as File;
    return Promise.resolve(`bileli-${assetId}-${f.name}-${f.size}-${f.lastModified}`);
  };
}

export type OnPct = (pct: number) => void;

/** Direct-to-storage byte push (NO cookies) — per protocol. */
export async function uploadBytes(ticket: UploadTicket, file: File, onProgress?: OnPct): Promise<void> {
  if (ticket.protocol === 'S3_PUT') {
    const res = await fetch(ticket.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type }, // must match the type signed at initiate
    });
    if (!res.ok) throw new MediaApiError(`Ошибка загрузки в хранилище (${res.status}).`, res.status);
    onProgress?.(100);
    return;
  }

  if (ticket.protocol === 'CF_DIRECT_FORM') {
    const form = new FormData();
    form.append('file', file);
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', ticket.uploadUrl);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new MediaApiError(`Ошибка загрузки изображения (${xhr.status}).`, xhr.status));
      };
      xhr.onerror = () => reject(new Error('Ошибка загрузки изображения.'));
      xhr.send(form);
    });
    return;
  }

  // BUNNY_TUS — resumable
  const t = ticket;
  await new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: t.creationUrl,
      retryDelays: [0, 1000, 3000, 5000],
      fingerprint: fingerprintFor(t.asset.id),
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      metadata: { filetype: file.type, title: file.name },
      headers: {
        AuthorizationSignature: t.authorizationSignature,
        AuthorizationExpire: String(t.authorizationExpire),
        VideoId: t.videoGuid,
        LibraryId: String(t.libraryId),
      },
      onError: (error) => reject(error),
      onProgress: (bytesUploaded, bytesTotal) => {
        if (onProgress && bytesTotal > 0) onProgress(Math.round((bytesUploaded / bytesTotal) * 100));
      },
      onSuccess: () => resolve(),
    });
    upload.findPreviousUploads()
      .then((previous) => {
        if (previous.length > 0) upload.resumeFromPreviousUpload(previous[0]);
        upload.start();
      })
      .catch(() => { upload.start(); }); // localStorage unavailable → fresh start
  });
}

// Bileli 2026-07-29 — replaced by a wall-clock deadline (model/finalize-poll.ts). The old constants:
//   const FINALIZE_MAX_ATTEMPTS = 60;
//   const FINALIZE_DELAY_MS = 5000;
//   const MAX_RETRY_AFTER_S = 60;
// 60 × 5s reads as "295 seconds", but it is not a duration — it is a product of two numbers, only one of
// which we control, so the budget SHRANK every time the backend sent a shorter Retry-After. Real Bunny
// waits are 10–17 minutes (free shared encoder, a settled decision), so the ceiling was reached mid-encode
// on every video and the poll simply stopped. Kept here because the arithmetic is the lesson.
// MAX_RETRY_AFTER_S lives on as MAX_POLL_S in finalize-poll.ts.

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * What one 409 told us. `queued` is the field that matters:
 * - `true`  → the video is sitting in Bunny's shared queue and has NOT started. `encodeProgress` is null
 *             by design, because Bunny pins it to 0 for the whole wait and rendering that as "0%"
 *             reports a stall that is not happening.
 * - `false` → really transcoding; `encodeProgress` is a real 0–100.
 *
 * ⚠ Never derive `queued` from `bunnyStatus`. Verified in production 2026-07-29: a video went
 * status 2 @ 5% straight to status 4, never reporting 3 at all — Bunny does real work under status 2, so
 * the status cannot separate the two waits. `bunnyStatus` is carried for diagnostics only.
 */
export type EncodeWait = { encodeProgress?: number; bunnyStatus?: number; queued: boolean };
export type OnEncodeProgress = (wait: EncodeWait) => void;

/**
 * The cheap status probe (`GET /media/uploads/{id}`, backend PR #342).
 *
 * ⚠ Returns the BARE asset — not finalize's `{ asset: … }` envelope — so it parses with MediaAssetSchema,
 * never FinalizeResponseSchema.
 */
export async function fetchUpload(mediaId: string): Promise<MediaAsset> {
  const raw = await bileliApiJson<unknown>(`/media/uploads/${mediaId}`);
  return parseApi(schemas.MediaAssetSchema, raw, 'GET /media/uploads/{id}') as MediaAsset;
}

/**
 * Settle an upload, waiting out Bunny's queue.
 *
 * Shape: POST finalize once — it is what performs the PENDING→READY transition and the only thing that
 * reports `queued`/`encodeProgress` — then wait on the CHEAP GET, because finalize calls the vendor on
 * every single invocation while the GET reads a stored row (and reflects the Bunny webhook, which now
 * actually fires). finalize is re-POSTed every REFRESH_PROGRESS_EVERY ticks so the UI can flip from
 * "queued" to a real percentage.
 *
 * Readiness is NEVER gated on `encodeProgress` — it has no monotonicity guarantee and 0 → 100 with
 * nothing in between is normal. We wait for a 200 or a READY state.
 *
 * 400 = TERMINAL (asset FAILED / bytes never arrived) → MediaApiError(400). Hitting the deadline throws a
 * plain Error, which the engine treats as transient: it re-arms on a later tick and leaves the last known
 * state on screen. For S3/CF this is normally an immediate 200 and none of the loop runs.
 */
export async function finalizeUpload(mediaId: string, onProgress?: OnEncodeProgress): Promise<MediaAsset> {
  const startedAt = Date.now();
  let tick = 0;

  while (true) {
    const settled = await postFinalize(mediaId, onProgress);
    if (settled.asset) return settled.asset;
    const delayMs = settled.delayMs; // the server's adaptive cadence: long while queued, short while encoding

    // Wait on the cheap probe until the deadline; re-POST finalize periodically for the display.
    while (!isPastDeadline(startedAt, Date.now())) {
      await wait(delayMs);
      tick += 1;
      if (shouldRefreshProgress(tick)) break; // → re-POST finalize above
      const probe = probeFromState((await fetchUpload(mediaId).catch(() => undefined))?.state);
      if (probe === 'ready') return fetchUpload(mediaId);
      if (probe === 'failed') {
        throw new MediaApiError('Не удалось финализировать загрузку (400).', 400);
      }
    }
    if (isPastDeadline(startedAt, Date.now())) {
      throw new Error('Загрузка не завершилась вовремя.'); // transient by contract — the engine re-arms
    }
  }
}

/** One finalize POST. Returns the settled asset, or the wait it reported. */
async function postFinalize(
  mediaId: string, onProgress?: OnEncodeProgress,
): Promise<{ asset?: MediaAsset; delayMs: number }> {
  const res = await bileliApiFetch(`/media/uploads/${mediaId}/finalize`, { method: 'POST', headers: JSON_HEADERS });
  if (res.ok) {
    const body = parseApi(schemas.FinalizeResponseSchema, await res.json(), 'POST /media/uploads/{id}/finalize');
    return { asset: body.asset as MediaAsset, delayMs: DEFAULT_POLL_MS };
  }
  if (res.status !== 409) {
    throw new MediaApiError(`Не удалось финализировать загрузку (${res.status}).`, res.status);
  }
  let problem: { encodeProgress?: number; bunnyStatus?: number; queued?: boolean } = {};
  try {
    problem = (await res.json()) as typeof problem;
  } catch { /* no body */ }
  onProgress?.({
    encodeProgress: problem.encodeProgress,
    bunnyStatus: problem.bunnyStatus,
    // Absent on an older backend ⇒ treat as NOT queued, so we keep the pre-existing percentage behaviour
    // rather than showing "в очереди" forever against a server that cannot tell us.
    queued: problem.queued === true,
  });
  return { delayMs: nextPollDelayMs(res.headers.get('Retry-After')) };
}

/** Foreground upload end-to-end (PDF / IMAGE): initiate → bytes → finalize → mediaId. */
export async function uploadMedia(orgId: string, kind: MediaKind, file: File, onProgress?: OnPct): Promise<string> {
  validateFile(kind, file);
  const ticket = await initiateUpload({ orgId, kind, contentType: file.type, originalFilename: file.name });
  await uploadBytes(ticket, file, onProgress);
  const asset = await finalizeUpload(ticket.asset.id);
  return asset.id;
}

/**
 * Background VIDEO upload: initiate + persist the File (IndexedDB) and the tus ticket (localStorage),
 * return the PENDING mediaId immediately. The engine's resume driver pushes the bytes (kick it after
 * calling this); a reload resumes from the server byte offset.
 */
export async function initiateVideoUpload(orgId: string, file: File): Promise<string> {
  validateFile('VIDEO', file);
  const ticket = await initiateUpload({ orgId, kind: 'VIDEO', contentType: file.type, originalFilename: file.name });
  if (ticket.protocol !== 'BUNNY_TUS') throw new Error(`Неожиданный протокол загрузки (${ticket.protocol}).`);
  await putUploadFile(ticket.asset.id, file);
  savePendingUpload({
    assetId: ticket.asset.id,
    kind: 'VIDEO',
    contentType: file.type,
    filename: file.name,
    ticket: {
      creationUrl: ticket.creationUrl,
      tusVersion: ticket.tusVersion,
      libraryId: ticket.libraryId,
      videoGuid: ticket.videoGuid,
      authorizationSignature: ticket.authorizationSignature,
      authorizationExpire: ticket.authorizationExpire,
      uploadMetadataBase64: ticket.uploadMetadataBase64,
    },
  });
  return ticket.asset.id;
}
